<template>
	<div>
		<div class="d-flex flex-column">
			<div class="d-flex align-items-center">
				<FlexiblePaymentConditionPill :content="props.content" />
				<span
					class="caption text-grey-700 text-nowrap ms-2 d-flex align-items-center"
					v-if="props.prestId">
					<template v-if="isDisplayPlaceholders">
						<PlaceholderBox
							:height="'25px'"
							:width="'100px'" />
					</template>
					<template v-else> Ref: {{ props.prestId }} </template>
				</span>
			</div>
			<span
				v-if="props.displayTitle && !isCompactDisplay"
				class="caption-strong mt-2 d-flex align-items-center">
				{{ props.content.flex_title }}
			</span>
		</div>

		<div v-if="!isCompactDisplay" class="mt-2 subcaption d-flex align-items-center">
			<PackageDetailsTooltip @click="handleOpenPopup">
				<div>
					<span
						v-for="(sentence, sentence_i) in sentences"
						:key="`${sentence[0]}-${sentence_i}`">
						{{ sentence }}
					</span>
					<TermsAndConditions />
				</div>
			</PackageDetailsTooltip>
			<div>
				{{ title }} 
				<TermsAndConditions />
			</div> 
		</div>
		<DynamicPopup
			ref="popupRef"
			:overlay-color="'rgba(13, 20, 27, 0.95)'"
			:overlay-max-opacity="1">
			<PaymentConditionPopup @close="handleClosePopUp">
				<template #title>
					<div class="d-flex align-items-center gap-2">
						<FlexiblePaymentConditionPill
							:content="props.content"
							:height="30" />
						<div class="title-3">
							{{ props.content.flex_title }}
						</div>
					</div>
				</template>
				<template #content>
						<span
							v-for="(sentence, sentence_i) in sentences"
							:key="`${sentence[0]}-${sentence_i}`">
							{{ sentence }}
						</span>
						<TermsAndConditions />
				</template>
			</PaymentConditionPopup>
		</DynamicPopup>
	</div>
</template>

<script setup lang="ts">
import { defineProps, computed, ComputedRef, ref, Ref, onMounted } from "vue"
import type { ComponentPublicInstance } from "vue"
import moment from "moment"

// Utils
import { isScreenMD } from "@/vue/planning/utils/breakpoints"
import formatDateString from "@/vue/planning/utils/formatDate"

// Components
import FlexiblePaymentConditionPill from "./FlexiblePaymentConditionPill.vue"
import PlaceholderBox from "@/vue/planning/components/common/placeholderBox.vue"
import PackageDetailsTooltip from "./PackageDetailsTooltip.vue"
import DynamicPopup from "@/vue/common/DynamicPopup.vue"
import PaymentConditionPopup from "./PaymentConditionPopup.vue"
import TermsAndConditions from "./TermsAndConditions.vue"

// Composables
import useLocale from "@/vue/composables/useLocale"

const locale = useLocale()

// Props
interface Props {
	displayTitle: boolean
	content: { [key: string]: string }
	isPVFlex?: boolean
	prestId?: string
	isDisplayPlaceholders?: boolean
	cancellationDate?: string
	startDate?: string
	isCompactDisplay?: boolean
}

const props = defineProps<Props>()

// Refs
type DynamicPopupInstance = ComponentPublicInstance<typeof DynamicPopup>

const popupRef: Ref<DynamicPopupInstance | null> = ref(null)

// Computeds
const isStartDateMoreThen40Days: ComputedRef<boolean> = computed(() => {
	if (!props.startDate) {
		return false // Handle cases where startDate might not be provided
	}


	const startDate = moment(props.startDate, 'DD/MM/YYYY', true)
	const today = moment()

	if (!startDate.isValid()) {
		console.error("Invalid date format for startDate: ", props.startDate);
		return false;
	}

	// Calculate the difference in days
	const diffDays = startDate.diff(today, 'days')
	return diffDays > 40
})

const content: ComputedRef<{ [key: string]: string }> = computed(() => {
	return props.content
})

const formattedCancellationDate: ComputedRef<string> = computed(() => {
	return props.cancellationDate ? formatDateString(props.cancellationDate) : ""
})

const baseTitle: ComputedRef<string> = computed(() => {
	const base = props.isPVFlex
		? content.value.flex_line_1_PV
		: content.value.flex_line_1
		
	return `${base} ${formattedCancellationDate.value}${content.value.flex_line_2 || ''}`
})

const title: ComputedRef<string> = computed(() => {
	return baseTitle.value
})

// Methods
const getPVCancellationPolicy = (): string => {
	const cancellationDateFormatted = formattedCancellationDate.value
	return `${
		content.value.flex_picto_PV_cancelation_until ?? ""
	} 
	${cancellationDateFormatted}
		
	${
		content.value.flex_picto_PV_i_terms_conditions_1 ?? ""
	} 
	`
}

const getPVFirstSentence = (): string => {
	return isStartDateMoreThen40Days.value
		? content.value.flex_picto_PV_i_payment_condition_before_deposit
		: content.value.flex_picto_PV_i_payment_condition_after_deposit
}

const getDefaultSentences = (): string[] => {
	const cancellationDateFormatted = formattedCancellationDate.value
	return [
		`${content.value.flex_picto_i_1 ?? ""} ${locale !== "nl_NL" ? cancellationDateFormatted: ''} ${
			content.value.flex_picto_i_1_2 ?? ""
		}`,
		`${
			content.value.flex_picto_i_terms_conditions_1 ?? ""
		}`,
	]
}

// Computed property using helper methods with TypeScript type
const sentences: ComputedRef<string[]> = computed(() => {
	if (props.isPVFlex) {
		return [getPVFirstSentence(), getPVCancellationPolicy()]
	} else {
		return getDefaultSentences()
	}
})

// Methods
const handleOpenPopup = (): void => {
	// Only open the popup if the device is mobile
	if (isScreenMD.value) return

	if (popupRef.value) {
		popupRef.value.openPopup()
	}
}

const handleClosePopUp = () => {
	if (popupRef.value) {
		popupRef.value.closePopup()
	}
}

</script>
